import '@/styles/fonts.less';
import '@/styles/antd.min.css';
import '@/styles/globals.less';
import 'nprogress/nprogress.css';

import { CAMPAIGN_DATA, INTERCOM_APP_ID, STATIC_FILE_URL, WEBSITE_URL } from '@/shared/constants';
import { ConfigProvider, CreateAccountProvider } from '@/lib/context';
import React, { useEffect, useMemo } from 'react';

import { App as AntdApp } from 'antd';
import { ApolloProvider } from '@apollo/client';
import type { AppProps } from 'next/app';
import { GoogleTagManager } from '@next/third-parties/google';
import Head from 'next/head';
import { HistoryListProvider } from '@/lib/context/HistoryListProvider';
import { IntercomProvider } from 'react-use-intercom';
import NProgress from 'nprogress';
import { NextPage } from 'next';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { init as initApm } from '@elastic/apm-rum';
import isDev from '@/shared/hooks/utils/isDev';
import { isbot } from 'isbot';
import { setAnonymousUser } from '@/shared/utils/utils';
import storage from '@/shared/utils/storage';
import { useGraphQLClient } from '@/lib/hooks/useCreateGraphQLClient';
import { useRouter } from 'next/router';
import withTheme from '@/scripts/theme';
const serviceVersion = require('../../package.json').version;

if (typeof window !== 'undefined') {
  initApm({
    serviceName: 'pietra-landing-pages-js',
    // source map https://www.elastic.co/guide/en/observability/8.17/apm-source-map-how-to.html
    serviceVersion,
    active: !isDev && !isbot(navigator.userAgent),
    serverUrl: process.env.NEXT_PUBLIC_ELASTIC_APM_SERVER_URL,
    environment: process.env.NEXT_PUBLIC_ENV,
    distributedTracingOrigins: [
      process.env.NEXT_PUBLIC_API_HOST,
      process.env.NEXT_PUBLIC_GRAPHQL_API_HOST,
      process.env.NEXT_PUBLIX_AI_API_HOST,
    ],
  });
}

const ErrorBoundary = dynamic(() => import('@/components/Common/ErrorBoundary'));

NProgress.configure({ showSpinner: false });

export type NextPageWithLayout<P = {}> = NextPage<P> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  content?: any;
};

type AppPropsWithLayout<P = {}> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
};

export default function App({
  Component,
  pageProps,
}: AppPropsWithLayout<{ userAgent?: string; head?: any; props?: any }>) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  const router = useRouter();
  const apolloClient = useGraphQLClient({});

  useEffect(() => {
    setAnonymousUser();

    const handleStart = () => {
      NProgress.start();
    };

    const handleStop = () => {
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  useEffect(() => {
    const query = router.query;
    if (
      query &&
      (query.hasOwnProperty('utm_campaign') ||
        query.hasOwnProperty('utm_medium') ||
        query.hasOwnProperty('utm_source') ||
        query.hasOwnProperty('utm_content'))
    ) {
      storage.set(CAMPAIGN_DATA, {
        campaign: query['utm_campaign'] || '',
        campaignSource: query['utm_source'] || '',
        campaignMedium: query['utm_medium'] || '',
        campaignContent: query['utm_content'] || '',
      });
    }
  }, [router.query]);

  const asPath = router.asPath ?? router.route;

  const tagline = 'Pietra | AI Software and services for small businesses';
  const description = 'Get the best prices on all the tools you need to run a business.';
  const headerImage = `${STATIC_FILE_URL}/8f83f11283ff5a8074adb986e14272f0.png`;

  return (
    <>
      {useMemo(() => {
        return (
          <Head>
            <title key="title">{tagline}</title>
            <meta name="google-site-verification" content="l06Td3J_TNaxRTV30x0y6il60iJxZSxj-4IWylsnJrQ" />
            <meta key="description" name="description" content={description} />
            <meta key="og:url" property="og:url" content={WEBSITE_URL} />
            <meta property="og:type" content="website" />
            <meta key="og:title" property="og:title" content={tagline} />
            <meta key="og:description" property="og:description" content={description} />

            <meta key="og:image" property="og:image" content={headerImage} />
            <meta name="theme-color" content="#ffffff" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta key="twitter:domain" property="twitter:domain" content="pietrastudio.com" />
            <meta key="twitter:url" property="twitter:url" content={WEBSITE_URL} />
            <meta key="twitter:title" name="twitter:title" content={tagline} />
            <meta key="twitter:description" name="twitter:description" content={description} />
            <meta key="twitter:image" name="twitter:image" content={headerImage} />
            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=yes" />
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-title" content="pietra-website" />
            <link rel="mask-icon" href={`${STATIC_FILE_URL}/321a869e20aa62283f1ba7c562419651.svg`} color="#FF5C3C" />
            <link rel="icon" href="/favicon.ico" />
            <link
              rel="apple-touch-icon"
              sizes="256x256"
              href={`${STATIC_FILE_URL}/ea654fc8f679ea800871e264a602e86a.png`}
            />
            <link rel="canonical" href={asPath === '/' ? WEBSITE_URL : WEBSITE_URL + asPath} />
          </Head>
        );
      }, [asPath])}

      {withTheme(
        <ErrorBoundary>
          <ApolloProvider client={apolloClient}>
            <AntdApp>
              <ConfigProvider>
                {/* IMPORTANT: Must use 0.0.0.0:3000 in order to test Intercom */}
                <IntercomProvider appId={INTERCOM_APP_ID}>
                  <HistoryListProvider>
                    <CreateAccountProvider>{getLayout(<Component {...pageProps} />)}</CreateAccountProvider>
                  </HistoryListProvider>
                  <GoogleTagManager gtmId="GTM-576D7WT" />
                  <Script
                    strategy="lazyOnload"
                    src="https://swtus.b-cdn.net/swt-mjkoxrjyaftnzrwkeafyn.latest.js"
                  ></Script>
                </IntercomProvider>
              </ConfigProvider>
            </AntdApp>
          </ApolloProvider>
        </ErrorBoundary>,
      )}
    </>
  );
}
